import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  Grid,
  Tooltip,
  TableFooter,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Badge,
} from "@mui/material";
import {
  Search,
  Close,
  Person,
  Payment,
  Receipt,
  AccountBalance,
  Warning,
  ArrowForward,
  Visibility,
  Bed,
  CalendarToday,
  Phone,
  Email,
  Cancel,
  Delete,
  Hotel,
  MeetingRoom,
  AccessTime,
  CheckCircle,
  DateRange,
} from "@mui/icons-material";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  getCustomersOrders_pending,
  getCustomersWithPendingTransactions,
} from "../../../api/customers/customers";
import { IonApp, IonButton, IonContent, IonPage, useIonViewWillEnter } from "@ionic/react";
import axios from "axios";
import { baseUrl, config, requestID } from "../../../common/utils";

// Room reservation interface based on actual response structure
interface RoomReservation {
  order_id: string;
  reservation_id: string;
  customer_id: string;
  room_id?: string;
  room_name?: string;
  room_type?: string;
  member_name: string;
  customer_phone: string;
  customer_email: string;
  check_in_date?: string;
  check_out_date?: string;
  total_nights?: number;
  total_amount: number;
  amount_paid: number;
  balance_due: number;
  reservation_status: string;
  created_at: string;
  transaction_count: number;
  member_id?: string;
  product_name?: string;
  product_type?: string;
}

// Cancel reservation modal props
interface CancelDialogProps {
  open: boolean;
  onClose: () => void;
  reservation: RoomReservation | null;
  onConfirm: () => void;
  loading: boolean;
}

const CancelReservationDialog: React.FC<CancelDialogProps> = ({
  open,
  onClose,
  reservation,
  onConfirm,
  loading
}) => {
  // Extract room name from reservation data
  const getRoomName = (res: RoomReservation) => {
    if (res.room_name) return res.room_name;
    if (res.product_name) return res.product_name;
    return `Reservation ${res.reservation_id || res.order_id}`;
  };

  const getDates = (res: RoomReservation) => {
    if (res.check_in_date && res.check_out_date) {
      return `${new Date(res.check_in_date).toLocaleDateString()} - ${new Date(res.check_out_date).toLocaleDateString()}`;
    }
    return "Dates not specified";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning />
          Cancel Reservation
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {reservation && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Are you sure you want to cancel this reservation? This action cannot be undone.
            </Alert>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Reservation Details:</Typography>
              <Typography variant="body2" fontWeight="500">
                {getRoomName(reservation)}
                {reservation.room_type && (
                  <Chip 
                    size="small" 
                    label={reservation.room_type} 
                    sx={{ ml: 1 }} 
                    color="primary" 
                    variant="outlined" 
                  />
                )}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Guest:</strong> {reservation.member_name}
              </Typography>
              <Typography variant="body2">
                <strong>Phone:</strong> {reservation.customer_phone}
              </Typography>
              {reservation.customer_email && (
                <Typography variant="body2">
                  <strong>Email:</strong> {reservation.customer_email}
                </Typography>
              )}
              {reservation.check_in_date && reservation.check_out_date && (
                <Typography variant="body2">
                  <strong>Dates:</strong> {getDates(reservation)}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Total Amount:</strong> {formatCurrencyN(reservation.total_amount)}
              </Typography>
              <Typography variant="body2">
                <strong>Amount Paid:</strong> {formatCurrencyN(reservation.amount_paid)}
              </Typography>
              <Typography variant="body2" color="error.main" fontWeight="bold">
                <strong>Balance Due:</strong> {formatCurrencyN(reservation.balance_due)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Reservation ID:</strong> {reservation.reservation_id || reservation.order_id}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Keep Reservation
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          startIcon={<Cancel />}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Cancel Reservation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RoomReservationsPage: React.FC = () => {
  const history = useHistory();
  const [roomReservations, setRoomReservations] = useState<RoomReservation[]>([]);
  const [filteredReservations, setFilteredReservations] = useState<RoomReservation[]>([]);
  const [loading, setLoading] = useState({
    reservations: false,
    canceling: false,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    reservation: null as RoomReservation | null,
  });

  // Fetch room reservations on component mount
  useEffect(() => {
    fetchRoomReservations();
  }, []);

  useIonViewWillEnter(() => {
    fetchRoomReservations();
  });

  const fetchRoomReservations = async () => {
    try {
      setLoading((prev) => ({ ...prev, reservations: true }));
      
      // Call the actual endpoint from your API
      const response = await getCustomersOrders_pending();
      console.log("Fetched pending transactions (reservations):", response.data);
      
      // Transform the API response to RoomReservation format
      if (response.data.orders && Array.isArray(response.data.orders)) {
        const transformedReservations = response.data.orders.map((item: any) => {
          // Extract reservation details from your API response
          const reservation: RoomReservation = {
            order_id: item.order_id || `ORD${Math.random().toString(36).substr(2, 9)}`,
            reservation_id: item.reservation_id || item.order_id || `RES${Math.random().toString(36).substr(2, 9)}`,
            customer_id: item.customer_id || item.member_id || `CUST${Math.random().toString(36).substr(2, 9)}`,
            member_name: item.member_name || "Guest",
            customer_phone: item.customer_phone || "No phone provided",
            customer_email: item.customer_email || "No email provided",
            total_amount: Number(item.payable_amount) || 0,
            amount_paid: Number(item.amount_paid) || 0,
            balance_due: Number(item.balance_due) || 0,
            reservation_status: item.reservation_status || item.status || 'pending',
            created_at: item.created_at || new Date().toISOString(),
            transaction_count: item.transaction_count || 1,
            member_id: item.member_id,
            
            // Room/Product specific fields (map from your data)
            room_name: item.product_name || item.room_name || "Room",
            room_type: item.product_type || item.room_type || "Standard",
            product_name: item.product_name,
            product_type: item.product_type,
            
            // Date fields - extract from your data or use defaults
            check_in_date: item.check_in_date || item.start_date || new Date().toISOString(),
            check_out_date: item.check_out_date || item.end_date || 
              new Date(new Date().getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(),
            total_nights: item.total_nights || Math.floor(Math.random() * 7) + 1,
          };
          
          return reservation;
        });
        
        setRoomReservations(transformedReservations);
        setFilteredReservations(transformedReservations);
      } else {
        console.error("Unexpected response format:", response.data);
        // Fallback to empty array
        setRoomReservations([]);
        setFilteredReservations([]);
      }
      
    } catch (error) {
      console.error("Error fetching room reservations:", error);
      // Set empty arrays on error
      setRoomReservations([]);
      setFilteredReservations([]);
    } finally {
      setLoading((prev) => ({ ...prev, reservations: false }));
    }
  };

  // Filter reservations based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredReservations(roomReservations);
      return;
    }

    const filtered = roomReservations.filter((reservation) => {
      const searchLower = searchQuery.toLowerCase();
      
      return (
        (reservation.member_name?.toLowerCase() || '').includes(searchLower) ||
        (reservation.customer_phone?.toLowerCase() || '').includes(searchLower) ||
        (reservation.customer_email?.toLowerCase() || '').includes(searchLower) ||
        (reservation.room_name?.toLowerCase() || '').includes(searchLower) ||
        (reservation.room_type?.toLowerCase() || '').includes(searchLower) ||
        (reservation.reservation_id?.toLowerCase() || '').includes(searchLower) ||
        (reservation.order_id?.toLowerCase() || '').includes(searchLower)
      );
    });

    setFilteredReservations(filtered);
  }, [searchQuery, roomReservations]);

  const handleProcessReservation = (reservation: RoomReservation) => {
    // Prepare customer object for processing
    const nameParts = reservation.member_name.split(' ');
    const customer = {
      id: reservation.customer_id,
      user_id: reservation.customer_id,
      firstName: nameParts[0] || reservation.member_name,
      lastName: nameParts.slice(1).join(' ') || "",
      phone: reservation.customer_phone,
      email: reservation.customer_email,
      grade_letter: "A",
    };

    // Pass reservation details along with customer
    history.push('/folder/raise-receipt/Add Receipt', { 
      customer,
      reservation,
      reservationType: 'room'
    });
  };

  const handleCancelReservation = (reservation: RoomReservation) => {
    setCancelDialog({
      open: true,
      reservation,
    });
  };

  const confirmCancelReservation = async () => {
    if (!cancelDialog.reservation) return;

    try {
      setLoading((prev) => ({ ...prev, canceling: true }));
      
      // Call your cancellation endpoint
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("reservation_id", cancelDialog.reservation.reservation_id);
      formData.append("order_id", cancelDialog.reservation.order_id);
      formData.append("customer_id", cancelDialog.reservation.customer_id);

      // Replace with your actual endpoint
      const response = await axios.post(
        baseUrl() + "reservations/cancel",
        formData,
        config
      );

      if (response.data.status === "success") {
        // Remove the cancelled reservation from the list
        setRoomReservations(prev => 
          prev.filter(res => res.reservation_id !== cancelDialog.reservation?.reservation_id)
        );
        setFilteredReservations(prev => 
          prev.filter(res => res.reservation_id !== cancelDialog.reservation?.reservation_id)
        );
        
        setCancelDialog({ open: false, reservation: null });
        
        // Show success message
        console.log("Reservation cancelled successfully");
      } else {
        throw new Error(response.data.message || "Failed to cancel reservation");
      }
    } catch (error) {
      console.error("Error cancelling reservation:", error);
      // Show error message (you can add a toast/snackbar here)
    } finally {
      setLoading((prev) => ({ ...prev, canceling: false }));
    }
  };

  const handleViewAllReservations = () => {
    history.push('/folder/all-room-reservations');
  };

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'pending': return 'warning';
      case 'confirmed': return 'success';
      case 'cancelled': return 'error';
      case 'completed': return 'info';
      case 'paid': return 'success';
      case 'unpaid': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'pending': return <AccessTime />;
      case 'confirmed': 
      case 'paid': 
      case 'completed': return <CheckCircle />;
      case 'cancelled': 
      case 'unpaid': return <Cancel />;
      default: return <AccessTime />;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const getDaysBetween = (startDate: string, endDate: string) => {
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      return 1;
    }
  };

  // Calculate totals from actual data
  const totalOutstanding = filteredReservations.reduce(
    (sum, item) => sum + Number(item.balance_due), 
    0
  );
  const totalReservations = filteredReservations.length;
  const totalNights = filteredReservations.reduce(
    (sum, item) => {
      if (item.check_in_date && item.check_out_date) {
        return sum + getDaysBetween(item.check_in_date, item.check_out_date);
      }
      return sum + (item.total_nights || 1);
    }, 
    0
  );
  const totalAmount = filteredReservations.reduce(
    (sum, item) => sum + Number(item.total_amount), 
    0
  );

  return (
    <IonPage className="pushDown">
      <IonContent>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Room Reservations
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage pending room reservations and payments
            </Typography>
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'warning.light' }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Badge badgeContent={totalReservations} color="error">
                    <Hotel sx={{ fontSize: 40, mb: 1 }} />
                  </Badge>
                  <Typography variant="h6">Pending Reservations</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {totalReservations}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'error.light' }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Warning sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Total Outstanding</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {formatCurrencyN(totalOutstanding)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'info.light' }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <DateRange sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Total Nights</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {totalNights}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'success.light' }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <MeetingRoom sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Total Value</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {formatCurrencyN(totalAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search and Actions */}
          <Box sx={{ 
            mb: 3, 
            display: "flex", 
            gap: 2, 
            alignItems: "center", 
            flexWrap: "wrap",
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 1
          }}>
            <TextField
              placeholder="Search by guest name, phone, room, or reservation ID..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300, flexGrow: 1 }}
            />
            
            <Button
              variant="outlined"
              onClick={handleViewAllReservations}
              startIcon={<AccountBalance />}
            >
              All Reservations
            </Button>
            
            <Typography variant="body2" color="text.secondary">
              Showing {filteredReservations.length} of {roomReservations.length} reservations
            </Typography>
          </Box>

          {/* Room Reservations Table */}
          {loading.reservations ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredReservations.length > 0 ? (
            <TableContainer component={Paper} elevation={2}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "primary.light", color: 'white' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>Room & Guest Details</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }} align="center">Contact Information</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }} align="center">Stay Period</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }} align="right">Financial Details</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReservations.map((reservation) => {
                    const roomName = reservation.room_name || reservation.product_name || "Room";
                    const nights = reservation.check_in_date && reservation.check_out_date 
                      ? getDaysBetween(reservation.check_in_date, reservation.check_out_date)
                      : reservation.total_nights || 1;
                    
                    return (
                      <TableRow
                        key={reservation.reservation_id || reservation.order_id}
                        sx={{
                          "&:hover": { 
                            backgroundColor: "action.hover",
                            transform: "translateY(-2px)",
                            transition: "all 0.2s ease"
                          },
                        }}
                      > 
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                            <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
                              <Bed sx={{ fontSize: 20, color: "white" }} />
                            </Avatar>
                            <Box>
                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Typography variant="subtitle1" fontWeight="600">
                                  {roomName}
                                </Typography>
                                {reservation.room_type && (
                                  <Chip
                                    size="small"
                                    label={reservation.room_type}
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                                <Chip
                                  size="small"
                                  icon={getStatusIcon(reservation.reservation_status)}
                                  label={reservation.reservation_status}
                                  color={getStatusColor(reservation.reservation_status) as any}
                                  sx={{ textTransform: 'capitalize' }}
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                ID: {reservation.reservation_id || reservation.order_id}
                              </Typography>
                              <Typography variant="body2" fontWeight="500" mt={1}>
                                Guest: {reservation.member_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Created: {formatDate(reservation.created_at)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
                              <Phone fontSize="small" />
                              <Typography variant="body2">
                                {reservation.customer_phone}
                              </Typography>
                            </Box>
                            {reservation.customer_email && reservation.customer_email !== "No email provided" && (
                              <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                <Email fontSize="small" />
                                <Typography variant="body2" color="text.secondary">
                                  {reservation.customer_email}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            {reservation.check_in_date ? (
                              <>
                                <Typography variant="body2" fontWeight="500">
                                  {formatDate(reservation.date_bags)}
                                </Typography>
                             
                               
                                <Chip
                                  size="small"
                                  icon={<CalendarToday />}
                                  label={`${nights} night${nights > 1 ? 's' : ''}`}
                                  color="info"
                                  variant="outlined"
                                  sx={{ mt: 1 }}
                                />
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Dates not specified
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box>
                            <Typography variant="body2">
                              Total: {formatCurrencyN(reservation.total_amount)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Paid: {formatCurrencyN(reservation.amount_paid)}
                            </Typography>
                            <Typography 
                              variant="body1" 
                              fontWeight="bold"
                              color="error.main"
                              sx={{ mt: 1 }}
                            >
                              Balance: {formatCurrencyN(reservation.balance_due)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title="Process Payment">
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<Payment />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleProcessReservation(reservation);
                                }}
                                sx={{ minWidth: 120 }}
                              >
                                Process
                              </Button>
                            </Tooltip>
                            {reservation.amount_paid < 1  && (
                              
                            
                            
                            <Tooltip title="Cancel Reservation">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelReservation(reservation);
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ backgroundColor: 'primary.light', color: 'white' }}>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'white' }}>
                      Totals ({filteredReservations.length} reservations)
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'white' }}>
                      {formatCurrencyN(totalOutstanding)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
              {searchQuery ? (
                <>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No reservations found matching your search
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Try adjusting your search terms
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={() => setSearchQuery("")}
                    startIcon={<Close />}
                  >
                    Clear Search
                  </Button>
                </>
              ) : loading.reservations ? (
                <CircularProgress />
              ) : (
                <>
                  <MeetingRoom sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No pending room reservations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All room reservations have been processed
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={handleViewAllReservations}
                    sx={{ mt: 2 }}
                  >
                    View All Reservations
                  </Button>
                </>
              )}
            </Paper>
          )}
        </Box>

        {/* Cancel Reservation Dialog */}
        <CancelReservationDialog
          open={cancelDialog.open}
          onClose={() => setCancelDialog({ open: false, reservation: null })}
          reservation={cancelDialog.reservation}
          onConfirm={confirmCancelReservation}
          loading={loading.canceling}
        />
      </IonContent>
    </IonPage>
  );
};

export default RoomReservationsPage;